import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";

const NotificationAlert = ({
  show,
  type,
  message,
  duration = 2500,
  onClose,
}) => {
  const [progress, setProgress] = useState(100);
  const [paused, setPaused] = useState(false);

  const rafRef = useRef(null);
  const elapsedRef = useRef(0);

  const runTimer = () => {
    const startAt = performance.now();

    const loop = (now) => {
      if (paused) {
        return;
      }
      const delta = now - startAt;
      elapsedRef.current += delta;
      const percent = Math.max(100 - (elapsedRef.current / duration) * 100, 0);
      setProgress(percent);

      // close
      if (elapsedRef.current >= duration) {
        onClose?.();
        return;
      }
      runTimer();
    };

    rafRef.current = requestAnimationFrame(loop);
  };

  // Start fresh when alert appears
  useEffect(() => {
    if (show) {
      elapsedRef.current = 0;
      setProgress(100);
      runTimer();
    }

    return () => cancelAnimationFrame(rafRef.current);
  }, [show]);

  useEffect(() => {
    if (!show) return;

    cancelAnimationFrame(rafRef.current);

    if (!paused) {
      runTimer();
    }
  }, [paused]);

  const typeStyles = {
    success: {
      gradient: "from-green-400/80 to-emerald-500/80",
      text: "text-green-900",
      icon: <CheckCircle className="text-green-600" size={26} />,
      glow: "shadow-green-200",
      bar: "bg-green-500",
    },
    error: {
      gradient: "from-red-400/80 to-rose-500/80",
      text: "text-red-900",
      icon: <XCircle className="text-red-600" size={26} />,
      glow: "shadow-red-200",
      bar: "bg-red-500",
    },
    warning: {
      gradient: "from-yellow-400/80 to-amber-500/80",
      text: "text-yellow-900",
      icon: <AlertTriangle className="text-yellow-600" size={26} />,
      glow: "shadow-yellow-200",
      bar: "bg-yellow-500",
    },
    info: {
      gradient: "from-blue-400/80 to-indigo-500/80",
      text: "text-blue-900",
      icon: <Info className="text-blue-600" size={26} />,
      glow: "shadow-blue-200",
      bar: "bg-blue-500",
    },
  };

  const style = typeStyles[type] || typeStyles.info;

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Background */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9998] pointer-events-none"
          />
          {/* Toast */}
          <motion.div
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            initial={{ opacity: 0, x: 100, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, y: 50, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 180, damping: 20 }}
            className={`fixed bottom-8 right-8 z-[9998] p-[2px] rounded-2xl shadow-xl ${style.glow} bg-gradient-to-r ${style.gradient}`}
          >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl flex flex-col shadow-md border border-white/20 w-auto min-w-[340px] max-w-[700px] overflow-hidden">
              {/* Progress bar */}
              <div className="w-full h-1 bg-gray-200">
                <motion.div
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.2 }}
                  className={`h-full ${style.bar}`}
                />
              </div>

              {/* Content */}
              <div className="p-4 flex items-start gap-3">
                {style.icon}

                <div className="flex-1">
                  <p className={`font-medium text-base ${style.text}`}>
                    {message}
                  </p>
                </div>

                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationAlert;
